import { AUTH_API_HOST } from "@/api/apis";
import type { Token } from "@/models/User";
import { readGaIdentifiersFromBrowser } from "@/libs/analytics/ga-cookie";
import { CartToken } from "@/libs/cart-token/CartToken";
import {
  beginCartSync,
  endCartSync,
  markCartSyncPending,
} from "@/libs/cart/server-cart-sync-state";
import {
  claimedTokenToRotate,
  isCartMerged,
  isCartPrepared,
  isRememberedClaimedToken,
  markCartMerged,
  markCartPrepared,
  readStoredOptOut,
  rememberClaimedCartToken,
} from "@/libs/global-e/cart-prep";
import { useCartCouponStore } from "@/store/cart-coupon-store";

export type ServerCartItem = { sku: string; quantity: number };
// standalone 게임아이템(서버 확정 형식 mgmt #1164: 식별자만, quantity 없음).
// Global-E GetCheckoutCartInfo 가 IsVirtual 제품으로 읽고 가격/이름은 카탈로그에서 resolve.
export type ServerGameItem = {
  gameResellId: string;
  platform: string;
};

let cachedToken: string | null = null;
let tokenExpiresAt = 0;

/**
 * 캐시한 로그인 토큰을 버린다 — 게스트로 확정된 뒤(세션 만료 등) 이 모듈이 10분 캐시로 회원 토큰을 계속 쓰지
 * 않게 한다. `CartLoginSync` 의 토큰 교체(#6179 B)가 부른다.
 */
export function forgetCartAuthToken(): void {
  cachedToken = null;
  tokenExpiresAt = 0;
}

async function getToken(): Promise<string | null> {
  if (cachedToken && Date.now() < tokenExpiresAt) return cachedToken;
  try {
    const res = await fetch(`${AUTH_API_HOST}/api/token`, {
      credentials: "include",
    });
    if (!res.ok) return null;
    const result = await res.json();
    cachedToken = result.token;
    tokenExpiresAt = Date.now() + 10 * 60 * 1000;
    return cachedToken;
  } catch {
    return null;
  }
}

let debounceTimer: ReturnType<typeof setTimeout>;
// 마지막으로 syncCart 에 들어온 payload(debounce 대기 중). flushCartSync 가 즉시 밀어낼 때 사용.
let pendingSync: {
  items: ServerCartItem[];
  gameItems: ServerGameItem[];
} | null = null;
// 마지막으로 시작된 서버 동기화 promise — flushCartSync 가 in-flight 완료까지 기다릴 수 있게 추적.
let inFlightSync: Promise<void> = Promise.resolve();

/**
 * `bh_cart_token` 키의 게스트 카트에 items + standalone 게임아이템을 쓴다(무인증).
 * gameItems 는 Global-E GetCheckoutCartInfo 가 IsVirtual 제품으로 읽는다(§5.6, mgmt 스키마 확장 의존).
 * Global-E 가 이 토큰 카트를 읽으므로, 완료를 await 할 수 있게 promise 를 반환한다(flushCartSync).
 *
 * **게스트 카트 쓰기의 단일 관문이다** — `doSync`(debounced sync)와 `writeGuestCartForCheckout`
 * (buy-now full-nav)이 모두 여기로 모인다. GA4 식별자를 여기 한 곳에 실으면 GE 가 읽는 모든
 * 경로가 자동으로 덮인다(#5778).
 */
function postGuestCart(
  cartToken: string,
  items: ServerCartItem[],
  gameItems: ServerGameItem[] = [],
): Promise<void> {
  return fetch("/admin-api/api/v1/guest-cart/", {
    method: "POST",
    headers: { "content-type": "application/json" },
    // GA4 서버사이드 purchase(MP) 용 식별자 — mgmt 가 GE draft 생성 시 여기서 옮겨 싣는다
    // (mgmt#1276). GE draft 는 GE 서버가 만들어 브라우저 쿠키가 없어서 이 경유가 필요하다.
    // 쿠키가 없으면 키가 탈락하고, mgmt 는 빈 값이면 기존 값을 보존하므로 안전하다.
    body: JSON.stringify({
      cartToken,
      items,
      gameItems,
      ...readGaIdentifiersFromBrowser(),
    }),
  })
    .then(() => {})
    .catch(() => {});
}

/**
 * 실제 동기화 1회 수행 — uid 카트(로그인 primary, fire-and-forget) + guest-cart 토큰 미러(await 대상).
 * 토큰 미러 완료를 반환해 flushCartSync 가 기다릴 수 있게 한다.
 */
function doSync(
  items: ServerCartItem[],
  gameItems: ServerGameItem[],
): Promise<void> {
  return getToken().then((token) => {
    if (token) {
      // uid 카트: 앱 UI·교차기기용 primary. GE 는 이 경로를 안 읽으므로 fire-and-forget.
      fetch("/admin-api/api/v1/cart/", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ items, gameItems }),
      }).catch(() => {});
      // Global-E 읽기용 토큰 mirror. 아이템 있으면 토큰 보장(ensure), 비었으면 기존 토큰에만 반영.
      const cartToken =
        items.length > 0 || gameItems.length > 0
          ? CartToken.ensure()
          : CartToken.get();
      return cartToken
        ? postGuestCart(cartToken, items, gameItems).then(() =>
            claimIfUnclaimed(cartToken),
          )
        : undefined;
    }
    const cartToken = CartToken.get();
    if (!cartToken) return;
    return postGuestCart(cartToken, items, gameItems);
  });
}

/**
 * 로그인 사용자의 sync 가 쓴 토큰을 이 브라우저가 아직 claim 하지 않았으면 claim 한다(#6179 B).
 *
 * 로그인 준비(`prepareLoggedInCart`)는 로그인 전이 때 한 번 돈다 — 그때 카트가 비어 토큰이 없었으면 claim 할 것이 없다.
 * 이후 첫 상품을 담아 토큰이 새로 생기면(`CartToken.ensure`) 소유자 없는 문서가 되어, 새로고침 없이 장바구니로 가면 GE
 * 견적에 쿠폰이 빠진다. 새 문서는 `couponOptOut=false` 로 시작하므로 탭 세션의 선택이 「사용 안 함」일 때만 저장한다.
 */
async function claimIfUnclaimed(cartToken: string): Promise<void> {
  if (isRememberedClaimedToken(cartToken)) return;
  const claimed = await claimGuestCartOwner();
  if (claimed && readStoredOptOut()) await saveCouponSelection(true);
}

/**
 * `doSync` 를 `inFlightSync` 큐에 이어붙인다 — 직전 sync 완료 후 실행해 **최신 payload 가 마지막에
 * 기록되도록 순서를 보장**한다(동시 실행 시 stale 이 나중에 착지하는 것 방지). debounce timer 경로와
 * flushCartSync 가 **같은 큐**를 써서 비대칭이 없다.
 * 체인의 모든 링크에서 에러를 삼켜 `inFlightSync` 가 **절대 reject 하지 않게** 한다 — flushCartSync 를
 * await 하는 체크아웃이 sync 실패(예: 쿠키 잠긴 env 의 `CartToken.ensure` 동기 throw)로 멈추면
 * 안 되기 때문(reject → `handleCheckout` 중단 → loading 고정 → 버튼 영구 멈춤).
 */
function enqueueSync(items: ServerCartItem[], gameItems: ServerGameItem[]) {
  // 반영 상태(GE 바스켓 표식 게이트) — 큐에 올리는 순간부터 이 링크가 끝날 때까지 「진행 중」.
  // 링크마다 begin/end 를 짝짓는다: 앞 링크가 끝나도 뒤 링크가 남아 있으면 아직 끝이 아니다.
  // ⚠️ 체인의 요청(`/api/token` · guest-cart POST · claim)엔 타임아웃이 없다 — 하나가 매달리면 「진행 중」이 세션 내내 남아
  //   바스켓 표식이 영영 안 붙는다. 그래서 링크당 `CART_SYNC_SETTLE_CAP_MS` 뒤엔 「끝」으로 본다(늦게 끝나도 end 는 한 번만).
  beginCartSync();
  let ended = false;
  const end = () => {
    if (ended) return;
    ended = true;
    clearTimeout(cap);
    endCartSync();
  };
  const cap = setTimeout(end, CART_SYNC_SETTLE_CAP_MS);
  inFlightSync = inFlightSync
    .catch(() => {})
    .then(() => doSync(items, gameItems))
    .catch(() => {})
    .finally(end);
}

/**
 * 반영 1건을 「진행 중」으로 보는 상한. 정상 착지는 실측 0.3초 안팎(2026-11-05 prod — guest-cart POST 250~380ms)이라 넉넉히 잡는다.
 * 넘기면 표식을 다시 붙인다 = 종전 동작(GE 가 그때의 서버 카트를 그린다). 근거는 `src/libs/cart/server-cart-sync-state.ts`.
 */
export const CART_SYNC_SETTLE_CAP_MS = 5000;

/**
 * 지정 아이템을 **guest-cart(bh_cart_token)에만** 즉시 쓴다(await). uid `/cart`(로그인 primary)는
 * 건드리지 않는다 — buy-now/PDP 체크아웃이 GE 국제 체크아웃으로 라우팅될 때 GE 가 읽을 카트를 채우되,
 * 로그인 유저의 실제 카트를 오염시키지 않기 위함(docs §4.4 #2). guest-cart 미러는 이후 카트 활동 시
 * self-heal(cart-store subscribe → syncCart). postGuestCart 가 에러를 삼키므로 reject 하지 않는다.
 *
 * ⚠️ **`enqueueSync`/`inFlightSync` 순서 체인(#5015)을 의도적으로 우회한다** — full-nav 직전에 GE 가
 * 곧바로 읽을 카트이므로 debounce 없이 즉시 await 로 착지를 보장해야 하기 때문. 경쟁 write 와의 순서
 * 안전 근거: (a) **바로 결제**(cart 우회)는 debounced `syncCart` 가 없다 — 단 직전에 소비 token 가드가 카트를
 * 정리했다면(`clearCartIfConsumed`) 그 정리가 건 빈 sync 가 있을 수 있어, 정리 **안에서** `flushCartSync()` 로 먼저
 * 밀어낸다(token 이 없는 동안 밀어내 새 token 을 덮지 않게 — `consumed-cart.ts` `clearConsumedCart`). (b) **담고 결제**는
 * cart add 가 debounced `syncCart` 를 트리거하지만 이 write 와 **동일한 카트 스냅샷**이라 어느 쪽이
 * 나중에 착지해도 divergence 가 없다(같은 토큰·같은 items). 향후 buy-now 아이템과 debounced 내용이
 * 갈라지는 진입점이 생기면 이 가정이 깨지므로 그때 `enqueueSync` 경유로 통일할 것.
 */
export async function writeGuestCartForCheckout(
  items: ServerCartItem[],
  gameItems: ServerGameItem[] = [],
): Promise<void> {
  const cartToken = CartToken.ensure();
  if (!cartToken) return;
  await postGuestCart(cartToken, items, gameItems);
}

/**
 * 카트를 서버에 동기화한다(debounced 500ms, fire-and-forget). 실제 쓰기는 `doSync`.
 * - 로그인 유저: Bearer 로 `/api/v1/cart` (uid 카트, primary) + 토큰 guest-cart 에도 mirror.
 * - 게스트: `bh_cart_token` 으로 `/api/v1/guest-cart` (token 카트).
 *
 * 토큰 guest-cart mirror 이유: Global-E `GetCheckoutCartInfo` 는 Bearer 없이 cart token 만 받으므로
 * 토큰 한 경로로 게스트·로그인 카트를 모두 읽어야 한다. docs §3.1(B)·§4.0.
 * ⚠️ debounce/fetch 는 페이지 언로드로 취소되므로, GE 국제 체크아웃 full-nav 직전엔 `flushCartSync` 로
 *    강제 flush 할 것(안 하면 GE 가 stale 카트를 읽어 수량 어긋남 — #5013 후속).
 */
export function syncCart(
  items: ServerCartItem[],
  gameItems: ServerGameItem[] = [],
) {
  pendingSync = { items, gameItems };
  // ★debounce 를 **시작하는 순간**부터 「반영 전」이다 — GE 는 이 500ms 안에 옛 카트를 읽는다
  // (`src/libs/cart/server-cart-sync-state.ts`). 큐에 올리기 전에 진행 표시를 먼저 켜야 사이에 「끝」이 끼지 않는다.
  markCartSyncPending(true);
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    const p = pendingSync;
    pendingSync = null;
    if (p) enqueueSync(p.items, p.gameItems);
    markCartSyncPending(false);
  }, 500);
}

/**
 * 대기(debounced) 중이거나 진행 중인 카트 동기화를 **즉시 밀어내고 완료까지 기다린다**.
 * Global-E 국제 체크아웃으로 full-nav(`window.location.href`) 하기 직전에 호출 — 안 하면 페이지
 * 언로드가 debounce 타이머/in-flight fetch 를 취소해 GE 가 `GetCheckoutCartInfo` 로 이전(stale)
 * guest-cart 를 읽어 수량·항목이 어긋난다(#5013 후속 버그). `enqueueSync` 로 직전 sync 뒤에 이어붙여
 * 최신 payload 가 마지막에 쓰이도록 순서를 보장하고, 체인이 에러를 삼키므로 **실패해도 reject 하지
 * 않는다**(체크아웃을 막지 않게 — enqueueSync 참고).
 */
export async function flushCartSync(): Promise<void> {
  clearTimeout(debounceTimer);
  const p = pendingSync;
  pendingSync = null;
  if (p) enqueueSync(p.items, p.gameItems);
  // 큐에 올린 **뒤에** 끈다 — 순서가 바뀌면 그 사이 한 틱 동안 「끝」으로 읽힌다(syncCart 타이머와 같은 순서).
  markCartSyncPending(false);
  // enqueueSync 가 체인 끝에서 에러를 삼켜 inFlightSync 는 항상 resolve → 이 await 는 throw 하지 않는다.
  await inFlightSync;
}

/**
 * 로그인 유저가 현재 게스트 cart token 을 **자기 것으로 claim** — mgmt 가 `carts[cartToken].ownerUid`
 * 에 회원 uid 를 기록한다(uid 는 서버가 Bearer 에서만 도출, body 로 안 보냄 = uid 주입 표면 제거).
 * Global-E 국제 체크아웃 주문이 **회원으로 귀속**되게 하는 전제다(docs/global-e-send-order-contract.md
 * §2.5, mgmt #1179). `mergeGuestCart` 와 달리 **게스트 문서를 삭제하지 않으므로**(ownerUid 만 기록) GE 가
 * `GetCheckoutCartInfo`/`SendOrderToMerchant` 에서 그 토큰 카트를 계속 읽고 귀속할 수 있다.
 * 게스트 cartToken 이 없거나 로그인 토큰을 못 받으면 no-op. fire-and-forget.
 */
export async function claimGuestCartOwner(): Promise<boolean> {
  const cartToken = CartToken.get();
  if (!cartToken) return false;
  const token = await getToken();
  if (!token) return false;
  try {
    const res = await fetch("/admin-api/api/v1/cart/guest-token/owner/", {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ cartToken }),
    });
    // 회원 소유가 된 토큰을 기억한다 — 로그아웃 뒤 같은 브라우저의 게스트가 이 토큰을 이어 쓰면 이전 회원의
    // 쿠폰·주문 귀속이 따라간다. 게스트 확정 시 토큰을 바꾸는 근거(`src/libs/global-e/cart-prep.ts`, #6179).
    if (res.ok) rememberClaimedCartToken(cartToken);
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * 장바구니 쿠폰 선택(자동 적용 / 사용 안 함)을 mgmt `couponOptOut` 에 기록한다. 소유자 본인만 쓸 수 있다.
 * 게스트·cart token 없음은 할 일이 없어 `true`. 실패는 `false`.
 *
 * 이 요청이 끝나면 GE 가 장바구니를 새 선택으로 다시 견적한다 — URL 에 `cart` 가 있어서다(`src/libs/global-e/cart-prep.ts`).
 */
export function saveCouponSelection(optOut: boolean): Promise<boolean> {
  // 직렬화 — 빠른 연속 토글에서 **마지막 선택**이 서버에 남게 한다(요청이 앞질러 도착하면 이전 선택이 이긴다).
  // 결제 버튼의 저장(`prepareGeCheckoutCoupons`)도 이 줄 뒤에 선다.
  selectionChain = selectionChain.then(() => putCouponSelection(optOut));
  return selectionChain;
}

let selectionChain: Promise<boolean> = Promise.resolve(true);

async function putCouponSelection(optOut: boolean): Promise<boolean> {
  const cartToken = CartToken.get();
  if (!cartToken) return true;
  const token = await getToken();
  if (!token) return true;
  try {
    const res = await fetch(
      "/admin-api/api/v1/cart/guest-token/coupon-selection/",
      {
        method: "PUT",
        headers: {
          "content-type": "application/json",
          authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ cartToken, optOut }),
      },
    );
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * GE 국제 체크아웃으로 full-nav 하기 **직전에** 쿠폰 전제를 서버에 확정한다.
 *
 * GE 는 체크아웃을 띄우자마자 `GetCheckoutCartInfo` 로 가격을 읽고, 그 가격에 **카트 소유자의 발급
 * 쿠폰**이 녹는다(`libs/global-e/cart-coupons.ts`). 그래서 이 시점에 두 가지가 서버에 있어야 한다:
 *   1. **claim**(ownerUid) — `/international-checkout` 마운트 때도 부르지만 fire-and-forget 이라 GE
 *      iframe 의 첫 cart-info 보다 늦을 수 있다. 늦으면 그 가격엔 쿠폰이 없다. 그래서 여기서 **기다린다**.
 *      (멱등 — 같은 소유자의 재claim 은 아무것도 바꾸지 않는다.)
 *   2. **쿠폰 선택**(자동 적용 / 사용 안 함) — mgmt `couponOptOut`. 소유자 본인만 쓸 수 있다.
 *
 * 게스트(로그인 토큰 없음)·cart token 없음은 할 일이 없어 `true`. 선택 기록이 실패하면 `false` —
 * 호출부가 "사용 안 함" 이었다면 이동을 막아야 한다(안 막으면 원치 않는 쿠폰이 소진된다).
 */
export async function prepareGeCheckoutCoupons(
  optOut: boolean,
): Promise<boolean> {
  const cartToken = CartToken.get();
  if (!cartToken) return true;
  const token = await getToken();
  if (!token) return true;
  await claimGuestCartOwner();
  return saveCouponSelection(optOut);
}

/**
 * 로그인 직후, 로그아웃 상태로 담아둔 게스트 카트를 현재 uid 카트로 채택한다(전이 처리, §4.5).
 * 게스트 cartToken 이 없거나 로그인 토큰을 못 받으면 no-op.
 *
 * `preserveGuestCart: true` — 게스트(토큰) 문서를 **지우지 않는다**(mgmt#1338). GE 는 이 토큰 문서만 읽으므로,
 * 지우면 뒤이은 sync 가 문서를 새로 만들어 소유자·쿠폰 선택이 초기화되고 장바구니 첫 견적에 쿠폰이 빠진다(#6179).
 * ⚠️ mgmt#1338 이 없는 서버는 이 필드를 무시하고 종전처럼 지운다 — **FE prod 는 mgmt prod 배포 뒤**(#6179).
 */
export async function mergeGuestCart(): Promise<boolean> {
  const cartToken = CartToken.get();
  if (!cartToken) return false;
  const token = await getToken();
  if (!token) return false;
  try {
    const res = await fetch("/admin-api/api/v1/cart/merge/", {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ cartToken, preserveGuestCart: true }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * 로그인 사용자의 장바구니를 GE 견적에 맞게 서버에 준비한다(#6179 B) — 보존 merge → sync → claim → 쿠폰 선택.
 *
 * GE 견적에 쿠폰이 녹으려면 토큰 문서에 소유자(`ownerUid`)와 선택(`couponOptOut`)이 있어야 한다. 종전엔 merge·sync 를
 * 동시에 쏘고(같은 문서 lost-update) claim 은 결제 버튼에서만 해서, 장바구니 첫 화면엔 쿠폰이 빠졌다. 넷을 **순서대로**
 * 기다리고, 각 요청이 끝날 때 GE 가 다시 견적하므로 마지막 요청 뒤 견적이 최종값이다(`src/libs/global-e/cart-prep.ts`).
 *
 * `first: false`(이 탭 세션에서 이 토큰·회원의 준비가 이미 끝남)면 **sync 만** 한다. merge 를 매번 돌리지 않는 이유 —
 * 문서를 보존(`preserveGuestCart`)하므로 merge 가 매 로드 본문을 끝까지 돌고, mgmt `mergeGuestCart` 는 그때마다 uid 카트의
 * `reminderSentAt` 을 0 으로 되돌리고(장바구니 리마인더 「카트당 1회」가 풀린다) `CART_MERGED` 이벤트를 다시 적는다(#6185 리뷰,
 * mgmt `CartService.kt` main `mergeGuestCart`). 첫 준비 뒤로는 sync 가 매 로드 uid 카트를 쓰므로 merge 는 중복이다.
 * claim·선택 저장도 같은 이유(GE 재견적 호출 수)로 첫 준비에서만 한다.
 * 반환: 소유자 기록·선택 저장까지 성공했나(건너뛰면 `true`). 게스트·토큰 없음은 `false`.
 */
export async function prepareLoggedInCart(
  items: ServerCartItem[],
  opts: {
    first: boolean;
    /** 이 탭 세션에서 merge 가 이미 끝났나 — 첫 준비가 claim 실패로 미뤄져도 merge 는 반복하지 않는다(#6185 R4). */
    merged?: boolean;
    /** merge 가 성공했을 때 — 호출부가 세션 표시를 남긴다. */
    onMerged?: () => void;
  },
): Promise<boolean> {
  if (opts.first && !opts.merged && (await mergeGuestCart())) opts.onMerged?.();
  if (items.length > 0) {
    syncCart(items);
    await flushCartSync();
  }
  if (!opts.first) return true;
  const claimed = await claimGuestCartOwner();
  if (!claimed) return false;
  // 선택은 **저장 시점**의 탭 세션 값으로 쓴다 — 시작 때 읽어 들고 가면, 준비가 도는 동안 사용자가 바꾼 선택을 이 마지막
  // 저장이 덮어 화면과 서버(GE 합계)가 갈린다(#6185 R1). `claimIfUnclaimed` 와 같은 방식이다.
  return saveCouponSelection(readStoredOptOut());
}

/**
 * `_app` `CartLoginSync` 의 로그인 분기 — 세션 첫 준비 판정 · 화면 선택 복원 · 성공했을 때만 준비 완료 표시(#6179 B).
 * 판단을 여기 둬서 테스트 하네스로 잠근다(#6185 R1 — `_app` 안에선 뮤턴트 4종이 살아남았다).
 */
export async function runLoggedInCartPrep(
  items: ServerCartItem[],
  uid: string | undefined,
): Promise<boolean> {
  const cartToken = CartToken.get();
  const first = !cartToken || !uid || !isCartPrepared(cartToken, uid);
  // 탭 세션의 「사용 안 함」을 화면에도 되살린다 — 서버는 그 선택으로 견적하므로 화면만 auto 로 돌아가면 갈린다.
  if (readStoredOptOut())
    useCartCouponStore.getState().setSelection({ kind: "none" });
  const ok = await prepareLoggedInCart(items, {
    first,
    merged: !!cartToken && !!uid && isCartMerged(cartToken, uid),
    onMerged: () => {
      if (cartToken && uid) markCartMerged(cartToken, uid);
    },
  });
  // 실패하면 표시하지 않는다 — 표시되면 그 탭 세션 동안 다시 준비하지 않는다.
  if (ok && first && cartToken && uid) markCartPrepared(cartToken, uid);
  return ok;
}

/**
 * 서버에 저장된 현재 카트의 items(sku/quantity)를 읽어온다.
 * - 로그인 유저: Bearer 로 `/api/v1/cart`.
 * - 게스트: `bh_cart_token` 으로 `/api/v1/guest-cart`.
 * 서버는 sku/quantity 만 보관하므로, 로컬 카트로 복원하려면 호출 측에서
 * 카탈로그(sku→product)로 slug/options 를 해석해야 한다.
 */
export async function getCartInfo(): Promise<ServerCartItem[]> {
  const token = await getToken();
  try {
    if (token) {
      const res = await fetch("/admin-api/api/v1/cart/", {
        headers: { authorization: `Bearer ${token}` },
      });
      return parseItems(res);
    }
    const cartToken = CartToken.get();
    if (!cartToken) return [];
    const res = await fetch(
      `/admin-api/api/v1/guest-cart/?cartToken=${encodeURIComponent(cartToken)}`,
    );
    return parseItems(res);
  } catch {
    return [];
  }
}

/**
 * 이 cart token 이 **Global-E 주문으로 소비됐는지** 서버에 묻는다(mgmt `GET /api/v1/guest-cart/consumed`
 * — 그 token 으로 만든 `GlobalE` draft 가 있는가). 결제가 안 끝난 카트는 `false` 다.
 *
 * 판정 불가(네트워크 오류·타임아웃·mgmt 미배포 404·응답 형식 이상)는 **`null`** — 호출부는 비우지 않는다.
 * 모르면 종전 동작(카트 유지)으로 떨어지는 쪽이 안전하다: 잘못 비우면 결제 안 한 카트가 사라진다.
 * 소비 흐름은 `libs/global-e/consumed-cart.ts`, 근거는 docs/global-e-storefront.md §4.7.3.
 */
export async function getGuestCartConsumed(
  cartToken: string,
  timeoutMs: number,
): Promise<boolean | null> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  // 판정 불가는 **사유를 남긴다** — `null` 은 호출부에서 `false`(아직 결제 전)와 같은 동작이 되므로, 조용히 두면
  // "mgmt 미배포·배선 오류" 와 "정상" 이 밖에서 구별되지 않는다(docs/pitfalls.md 「fail-closed 경로는 조용히 반환하지 말 것」).
  // token 은 찍지 않는다.
  try {
    const res = await fetch(
      `/admin-api/api/v1/guest-cart/consumed/?cartToken=${encodeURIComponent(cartToken)}`,
      { signal: ctrl.signal },
    );
    if (!res.ok) {
      console.warn(
        `[GlobalE] consumed lookup unavailable (HTTP ${res.status})`,
      );
      return null;
    }
    const result = await res.json();
    const consumed = result?.message?.consumed;
    if (typeof consumed !== "boolean") {
      console.warn("[GlobalE] consumed lookup: unexpected response shape");
      return null;
    }
    return consumed;
  } catch (e) {
    console.warn(
      `[GlobalE] consumed lookup failed (${ctrl.signal.aborted ? "timeout" : "network"})`,
      e,
    );
    return null;
  } finally {
    clearTimeout(timer);
  }
}

async function parseItems(res: Response): Promise<ServerCartItem[]> {
  if (!res.ok) return [];
  const result = await res.json();
  const items = result?.message?.items;
  if (!Array.isArray(items)) return [];
  return items.map((i: { sku: string; quantity: number }) => ({
    sku: i.sku,
    quantity: i.quantity,
  }));
}

/**
 * 로그아웃 **직전**에 부른다 — 지금 cart token 을 「회원이 claim 한 토큰」으로 기억해 둔다. 로그아웃 뒤 게스트로 확정되면
 * `rotateCartTokenIfClaimed` 가 이 기억으로 토큰을 바꾼다.
 *
 * 이게 없으면 #6185 **이전 코드**가 claim 한 토큰(GE 결제 버튼 claim 은 기억을 남기지 않았다)은 로그아웃해도 바뀌지 않아,
 * 같은 브라우저의 다음 게스트·다른 회원 견적에 이전 회원의 쿠폰이 녹는다(#6179 준수 확인 2026-11-05). claim 여부를 몰라도
 * 직접 로그아웃이면 바꾸는 쪽이 안전하다 — 바뀌어도 상품은 옮겨 적고, 옛 토큰 문서는 지연 SendOrder 용으로 서버에 남는다.
 * ⚠️ 덮지 못하는 경로: 세션 만료(버튼을 안 누른 로그아웃) · 다른 탭에서의 로그아웃 · 저장소 차단.
 */
export function markCartTokenForLogout(): void {
  const cartToken = CartToken.get();
  if (cartToken) rememberClaimedCartToken(cartToken);
}

/**
 * 게스트로 **확정**된 브라우저의 cart token 이 회원이 claim 해 둔 것이면 새 토큰으로 바꾸고 카트를 옮겨 적는다(#6179 B).
 *
 * 장바구니만 봐도 claim 되므로(`prepareLoggedInCart`), 바꾸지 않으면 로그아웃 뒤 같은 브라우저의 게스트 견적에 이전
 * 회원의 쿠폰이 녹고 GE 주문도 그 회원에게 귀속된다(SendOrder 의 `ownerUid`). 옛 토큰 문서는 그대로 남는다 — 이 브라우저가
 * 더는 그 토큰을 쓰지 않을 뿐이다. 반환: 바꿨나.
 */
export function rotateCartTokenIfClaimed(
  token: Token | null | undefined,
  items: ServerCartItem[],
): boolean {
  // `useUser().token` 을 그대로 받는다 — 게스트 **확정**(`null`, 401)일 때만. 모름(`undefined`)을 로그아웃으로 읽으면
  // 일시 장애에 회원의 토큰이 바뀐다(`docs/pitfalls.md` #70 · `isConfirmedGuest`). 로그인(토큰 객체)도 그대로.
  if (token !== null) return false;
  if (!claimedTokenToRotate(CartToken.get())) return false;
  forgetCartAuthToken();
  CartToken.clear();
  if (items.length > 0) {
    CartToken.ensure();
    syncCart(items);
  }
  return true;
}
